import React from "react";

type ConfirmationDialogProps = {
  open: boolean;
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  pendingLabel: string;
  pending: boolean;
  tone?: "primary" | "warning";
  eyebrow?: string;
  note?: React.ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
};

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "a[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

export function ConfirmationDialog({
  open,
  title,
  description,
  confirmLabel,
  pendingLabel,
  pending,
  tone = "primary",
  eyebrow = "Publication control",
  note = "This changes the local bundle used by the site. It does not modify or delete the publication in GCS.",
  onCancel,
  onConfirm,
}: ConfirmationDialogProps): JSX.Element | null {
	const id = React.useId().replaceAll(":", "");
  const dialogRef = React.useRef<HTMLElement>(null);
  const cancelRef = React.useRef<HTMLButtonElement>(null);
  const returnFocusRef = React.useRef<HTMLElement | null>(null);
  const onCancelRef = React.useRef(onCancel);
  const pendingRef = React.useRef(pending);
  onCancelRef.current = onCancel;
  pendingRef.current = pending;

  React.useEffect(() => {
    if (!open) return undefined;
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.requestAnimationFrame(() => cancelRef.current?.focus());

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        if (!pendingRef.current) {
          event.preventDefault();
          onCancelRef.current();
        }
        return;
      }
      if (event.key !== "Tab" || !dialogRef.current) return;
      const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      window.requestAnimationFrame(() => returnFocusRef.current?.focus());
    };
  }, [open]);

  if (!open) return null;

  const titleID = `${id}-confirmation-title`;
  const descriptionID = `${id}-confirmation-description`;
  return (
    <div
      className="watchlist-modal-backdrop private-news-confirmation-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !pending) onCancel();
      }}
    >
      <section
        ref={dialogRef}
        className="investments-card watchlist-modal private-news-confirmation"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleID}
        aria-describedby={descriptionID}
      >
        <header className="watchlist-modal-header">
          <div>
            <div className="section-eyebrow">{eyebrow}</div>
            <h2 id={titleID}>{title}</h2>
          </div>
          <button
            type="button"
            className="swing-dialog-close"
            aria-label="Close confirmation"
            disabled={pending}
            onClick={onCancel}
          >
            Close
          </button>
        </header>
        <div className="private-news-confirmation-body">
          <p id={descriptionID}>{description}</p>
          {note && <div className="private-news-confirmation-note">{note}</div>}
        </div>
        <footer className="private-news-confirmation-actions">
          <button ref={cancelRef} type="button" className="swing-button secondary" disabled={pending} onClick={onCancel}>Cancel</button>
          <button
            type="button"
            className={`swing-button${tone === "warning" ? " warning" : ""}`}
            disabled={pending}
            onClick={onConfirm}
          >
            {pending ? pendingLabel : confirmLabel}
          </button>
        </footer>
      </section>
    </div>
  );
}
